import { useState, useCallback } from 'react';
import { Link } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import type { SignerAssignment } from '../types';
import { SignerService, PolicyService } from '../services/api';
import { useAsyncData } from '../hooks/useAsyncData';
import { getBrandingBaseUrl } from '../amplify-config';
import { formatDueDate } from '../utils/formatDueDate';
import { formatDate } from '../utils/formatDate';
import { displayPolicyTitle, isMissingPolicy, cleanPolicyTitle } from '../utils/displayPolicyTitle';
import { formatPolicyTitle } from '../lib/format-policy-title';
import { recordError } from '../lib/rum';
import EmptyState from '../components/EmptyState';
import LoadingPage from '../components/LoadingPage';
import SignTimeDetails from '../components/sign-time-details/SignTimeDetails';

/**
 * Confined Signer Portal shown to demo-provisioned signers (#2765). The demo
 * experience is limited to signing the single sample policy (reached directly
 * via the magic link), so the portal never exposes a general assignment queue.
 * Instead it explains the demo and invites the prospect to sign up for the full
 * product. Server-gated on the per-user `isDemo` flag in the signer-portal
 * response (#2795) — NOT the tenant-level flag, because in dev the demo tenant
 * is the integration fixture tenant, whose real signers must keep the queue.
 */
function DemoConfinedPanel() {
    // `ref=demo` (#3281) so the branding site forwards it to the TM `/signup`
    // endpoint and the DemoSignupAttributed funnel metric can be attributed.
    const signupUrl = `${getBrandingBaseUrl()}/signup?utm_source=demo&utm_medium=signer_demo&ref=demo`;
    return (
        <Box className="page-content" sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h4" component="h2" gutterBottom>
                You just experienced policy signing
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 560, mx: 'auto' }}>
                This is a demonstration of how your employees sign policies with Accedo — a single tap on their phone, a real Certificate of Completion, no app
                to install. Ready to roll it out to your team?
            </Typography>
            <Button variant="contained" href={signupUrl} component="a" sx={{ px: 4, py: 1.5, fontSize: '1rem' }}>
                Want this for your team? Get started free
            </Button>
        </Box>
    );
}

/** Card for a single pending assignment. */
function PendingCard({
    assignment,
    onViewDocument,
    loadingDocId,
}: {
    assignment: SignerAssignment;
    onViewDocument: (policyId: string) => void;
    loadingDocId: string | null;
}) {
    const signUrl = `/policies/${assignment.policyId}/sign?assignmentId=${assignment.assignmentId}`;
    const isLoading = loadingDocId === assignment.policyId;
    // Issue #1845 — humanize the backend `(unknown policy)` sentinel that
    // leaks through when the underlying policy has been deleted between
    // assignment and render.
    // Issue #3225 — humanize embedded ISO-8601 timestamps first (innermost),
    // then clean trailing tokens / map the missing-policy sentinel, matching
    // the order used on the Dashboard / Acknowledgments / Activity Log.
    const titleText = displayPolicyTitle(cleanPolicyTitle(formatPolicyTitle(assignment.policyTitle)));
    const missingPolicy = isMissingPolicy(assignment.policyTitle);
    // Issue #1860 — at 375px h6 lineHeight (~32px) makes a 3-line wrapped
    // title eat 96px of the card on its own; with 50 pending policies a
    // signer scrolls 50 viewport-heights. Drop to subtitle1 on mobile so
    // the title compresses to ~24px line-height without truncation — full
    // title stays visible, total card height drops from ~185px to ~145px.
    const isMobile = useMediaQuery('(max-width: 600px)');
    const titleVariant = isMobile ? 'subtitle1' : 'h6';
    return (
        <Card
            variant="outlined"
            // `signer-queue-row-{policyId}` — per-row stable handle for
            // tests/e2e/core-loop.spec.ts (#2335). policyId-keyed (not
            // assignmentId) because the e2e knows the policy it just
            // created; the assignment id is server-allocated.
            data-testid={`signer-queue-row-${assignment.policyId}`}
            sx={{
                mb: 2,
                borderLeft: assignment.isOverdue ? '4px solid' : undefined,
                borderLeftColor: assignment.isOverdue ? 'error.main' : undefined,
                bgcolor: assignment.isOverdue ? 'error.50' : undefined,
            }}
        >
            <CardContent>
                {/* flexWrap + rowGap on the OUTER box let the action row drop onto its own line
                    on narrow viewports (issue #1844) instead of staying beside the title and
                    squeezing it to ~30px wide, which combined with overflowWrap: 'anywhere'
                    caused titles to wrap one character per line. */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        rowGap: 1,
                    }}
                >
                    {/* minWidth: 0 lets this flex child shrink below intrinsic content width so long
                        unbreakable tokens in policyTitle don't push the action row past the right edge
                        on narrow viewports (issue #1799). */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant={titleVariant}
                            component="h3"
                            gutterBottom
                            sx={{
                                overflowWrap: 'anywhere',
                                // Issue #1845 — visually distinguish the fallback so signers can
                                // tell it's a placeholder, not a real policy title.
                                ...(missingPolicy ? { fontStyle: 'italic', color: 'text.secondary' } : {}),
                            }}
                        >
                            {titleText}
                        </Typography>
                        {assignment.dueDate && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: assignment.isOverdue ? 'error.dark' : 'text.primary',
                                        fontWeight: 600,
                                    }}
                                >
                                    Due: {formatDueDate(assignment.dueDate)}
                                </Typography>
                                {assignment.isOverdue && <Chip label="Overdue" color="error" size="small" sx={{ fontWeight: 700 }} />}
                            </Box>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Assigned: {formatDate(assignment.createdAt)}
                        </Typography>
                        {/* Issue #2838 — explain why the actions are disabled when the
                            underlying policy has been deleted. */}
                        {missingPolicy && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
                                This policy was removed by your organization.
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 2 }}>
                        {/* Issue #1845 — disable View Document when the underlying policy has been
                            deleted; there is no document to fetch. */}
                        {/* Issue #2906 — size="small" renders ~30px tall; enforce the app's 44px
                            mobile touch floor (same pattern as #1846) without changing the visible
                            typography. Desktop keeps the compact density. */}
                        <Button
                            variant="text"
                            size="small"
                            style={isMobile ? { minHeight: 44 } : undefined}
                            onClick={() => onViewDocument(assignment.policyId)}
                            disabled={isLoading || missingPolicy}
                        >
                            {isLoading ? 'Loading...' : 'View Document'}
                        </Button>
                        {/* Issue #2838 — when the policy is missing, Sign Now must not link to the
                            dead sign page (#1845 gated View Document but missed this). Render a
                            disabled plain button instead of a disabled Link: MUI keeps the href on
                            a disabled anchor, which stays middle-click/keyboard navigable. */}
                        {missingPolicy ? (
                            <Button variant="contained" disabled sx={{ whiteSpace: 'nowrap', minHeight: '44px' }}>
                                Sign Now
                            </Button>
                        ) : (
                            <Button component={Link} to={signUrl} variant="contained" sx={{ whiteSpace: 'nowrap', minHeight: '44px' }}>
                                Sign Now
                            </Button>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

/**
 * Inline Download Certificate button used by the Signed and Declined cards
 * on the signer portal (issue #1148). Calls
 * `PolicyService.downloadCertificate` which streams the PDF and triggers a
 * browser download. Stays small / inline to match the other card actions.
 */
function DownloadCertificateButton({ assignmentId }: { assignmentId: string }) {
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Issue #2906 — enforce the app's 44px mobile touch floor on this
    // size="small" action (same pattern as #1846). Desktop keeps the
    // compact density.
    const isMobile = useMediaQuery('(max-width: 600px)');

    const handleClick = async () => {
        setError(null);
        setDownloading(true);
        try {
            await PolicyService.downloadCertificate(assignmentId);
        } catch {
            setError('Failed to download.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Button
                variant="text"
                size="small"
                style={isMobile ? { minHeight: 44 } : undefined}
                onClick={handleClick}
                disabled={downloading}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {downloading ? 'Downloading…' : 'Download Certificate'}
            </Button>
            {error && (
                <Typography variant="caption" color="error" role="alert">
                    {error}
                </Typography>
            )}
        </Box>
    );
}

/** Card for a single completed assignment. */
function CompletedCard({
    assignment,
    onViewDocument,
    onViewDetails,
    loadingDocId,
}: {
    assignment: SignerAssignment;
    onViewDocument: (policyId: string) => void;
    onViewDetails: (assignment: SignerAssignment) => void;
    loadingDocId: string | null;
}) {
    const isLoading = loadingDocId === assignment.policyId;
    // Issue #1845 — humanize the backend `(unknown policy)` sentinel.
    // Issue #3225 — humanize embedded ISO-8601 timestamps first (innermost).
    const titleText = displayPolicyTitle(cleanPolicyTitle(formatPolicyTitle(assignment.policyTitle)));
    const missingPolicy = isMissingPolicy(assignment.policyTitle);
    // Issue #1858 — on mobile (<=600px), stack the card vertically so the
    // 3-button action row (View Details + Download Certificate + View
    // Document) gets full container width and its `flexWrap: 'wrap'` can
    // wrap the buttons across two rows. Without this, the row's intrinsic
    // width (~371px) exceeds the 343px content area on 375px viewports and
    // View Document clips past the right edge — the regression #1858 caught.
    const isMobile = useMediaQuery('(max-width: 600px)');
    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                {/* flexWrap + rowGap on the OUTER box let the action row drop onto its own line
                    on narrow viewports (issue #1844) instead of staying beside the title and
                    squeezing it to ~30px wide, which combined with overflowWrap: 'anywhere'
                    caused titles to wrap one character per line. */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        rowGap: 1,
                    }}
                >
                    {/* minWidth: 0 lets this flex child shrink below intrinsic content width so long
                        unbreakable tokens in policyTitle don't push the action row past the right edge
                        on narrow viewports (issue #1799). */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{
                                overflowWrap: 'anywhere',
                                // Issue #1845 — distinguish the fallback so signers can tell
                                // it's a placeholder, not a real title.
                                ...(missingPolicy ? { fontStyle: 'italic', color: 'text.secondary' } : {}),
                            }}
                        >
                            {titleText}
                        </Typography>
                        {assignment.acknowledgedAt && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Signed: {formatDate(assignment.acknowledgedAt)}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {/* Issue #2906 — size="small" actions render ~30px tall; enforce the
                            app's 44px mobile touch floor (same pattern as #1846) without
                            changing the visible typography. Desktop keeps the compact density. */}
                        <Button
                            variant="text"
                            size="small"
                            style={isMobile ? { minHeight: 44 } : undefined}
                            sx={{ whiteSpace: 'nowrap' }}
                            onClick={() => onViewDetails(assignment)}
                        >
                            View Details
                        </Button>
                        <DownloadCertificateButton assignmentId={assignment.assignmentId} />
                        {/* Issue #1845 — View Document is disabled when the underlying policy
                            has been deleted; the assignment row is still real but there is no
                            document to fetch. View Details and Download Certificate still work
                            because they operate on the assignment itself. */}
                        <Button
                            variant="outlined"
                            size="small"
                            style={isMobile ? { minHeight: 44 } : undefined}
                            sx={{ whiteSpace: 'nowrap' }}
                            onClick={() => onViewDocument(assignment.policyId)}
                            disabled={isLoading || missingPolicy}
                        >
                            {isLoading ? 'Loading...' : 'View Document'}
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

/** Card for a single declined assignment (issue #1146). Terminal — no Sign action.
 *  Issue #1858 — same mobile-stack pattern as CompletedCard so the 3-button
 *  action row gets full container width and View Document doesn't clip. */
function DeclinedCard({
    assignment,
    onViewDocument,
    onViewDetails,
    loadingDocId,
}: {
    assignment: SignerAssignment;
    onViewDocument: (policyId: string) => void;
    onViewDetails: (assignment: SignerAssignment) => void;
    loadingDocId: string | null;
}) {
    const isLoading = loadingDocId === assignment.policyId;
    // Issue #1845 — humanize the backend `(unknown policy)` sentinel.
    // Issue #3225 — humanize embedded ISO-8601 timestamps first (innermost).
    const titleText = displayPolicyTitle(cleanPolicyTitle(formatPolicyTitle(assignment.policyTitle)));
    const missingPolicy = isMissingPolicy(assignment.policyTitle);
    const isMobile = useMediaQuery('(max-width: 600px)');
    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                {/* flexWrap + rowGap on the OUTER box let the action row drop onto its own line
                    on narrow viewports (issue #1844) instead of staying beside the title and
                    squeezing it to ~30px wide, which combined with overflowWrap: 'anywhere'
                    caused titles to wrap one character per line. */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        rowGap: 1,
                    }}
                >
                    {/* minWidth: 0 lets this flex child shrink below intrinsic content width so long
                        unbreakable tokens in policyTitle don't push the action row past the right edge
                        on narrow viewports (issue #1799). */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{
                                overflowWrap: 'anywhere',
                                // Issue #1845 — distinguish the fallback so signers can tell
                                // it's a placeholder, not a real title.
                                ...(missingPolicy ? { fontStyle: 'italic', color: 'text.secondary' } : {}),
                            }}
                        >
                            {titleText}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip label="Declined" size="small" />
                            {assignment.declinedAt && (
                                <Typography variant="body2" color="text.secondary">
                                    on {formatDate(assignment.declinedAt)}
                                </Typography>
                            )}
                        </Box>
                        {assignment.declineReason && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Reason: {assignment.declineReason}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {/* Issue #2906 — size="small" actions render ~30px tall; enforce the
                            app's 44px mobile touch floor (same pattern as #1846) without
                            changing the visible typography. Desktop keeps the compact density. */}
                        <Button
                            variant="text"
                            size="small"
                            style={isMobile ? { minHeight: 44 } : undefined}
                            sx={{ whiteSpace: 'nowrap' }}
                            onClick={() => onViewDetails(assignment)}
                        >
                            View Details
                        </Button>
                        <DownloadCertificateButton assignmentId={assignment.assignmentId} />
                        {/* Issue #1845 — View Document is disabled when the underlying policy
                            has been deleted; View Details and Download Certificate still work. */}
                        <Button
                            variant="outlined"
                            size="small"
                            style={isMobile ? { minHeight: 44 } : undefined}
                            sx={{ whiteSpace: 'nowrap' }}
                            onClick={() => onViewDocument(assignment.policyId)}
                            disabled={isLoading || missingPolicy}
                        >
                            {isLoading ? 'Loading...' : 'View Document'}
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

/**
 * Signer portal page — shows the authenticated signer's pending and completed policy assignments.
 * Accessible to all authenticated users (signers and admins).
 */
function SignerPortal() {
    const loadAssignments = useCallback(() => SignerService.getAssignments(), []);
    const { data, error: loadFailure, loading, refetch: refetchAssignments } = useAsyncData('signer-assignments', loadAssignments);
    const error = loadFailure !== null ? 'Failed to load your policies. Please try again.' : null;
    const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
    // Sign-time provenance modal (issue #1147) — opens when the signer
    // clicks "View Details" on a signed or declined card. Only the
    // signer's own assignment is ever passed in (the backend filters by
    // the JWT-derived userId), so there is no cross-user disclosure.
    const [signTimeDetailsAssignment, setSignTimeDetailsAssignment] = useState<SignerAssignment | null>(null);

    /** Visible reload: the page falls back to its loader while it runs. */
    const fetchAssignments = useCallback(() => refetchAssignments(), [refetchAssignments]);

    const handleViewDocument = useCallback(async (policyId: string) => {
        // Open the tab SYNCHRONOUSLY inside the tap gesture, then redirect it
        // once the presigned URL resolves. Mobile Safari blocks
        // window.open(..., '_blank') called after an `await` (the user-gesture
        // context is gone), which silently no-op'd View Document on mobile
        // (#3084). Opening first and redirecting after keeps it within the gesture.
        const win = window.open('', '_blank');
        setLoadingDocId(policyId);
        try {
            const policy = await PolicyService.getPolicy(policyId);
            if (policy.presignedUrl) {
                if (win) {
                    win.location.href = policy.presignedUrl;
                } else {
                    // Pre-open was blocked (rare): last-ditch direct open.
                    window.open(policy.presignedUrl, '_blank');
                }
            } else {
                win?.close();
            }
        } catch (err) {
            recordError(err, { source: 'SignerPortal.handleViewDocument' });
            win?.close();
        } finally {
            setLoadingDocId(null);
        }
    }, []);

    if (loading) {
        return <LoadingPage />;
    }

    // #2765 / #2795 — demo-provisioned signers get a confined portal (no
    // assignment queue). Server-gated on the PER-USER `isDemo` flag carried
    // by the signer-portal response (set only by the demo provision Lambda),
    // so real signers — including fixture-tenant signers in dev, where the
    // demo tenant IS the fixture tenant — always keep their queue.
    if (data?.isDemo) {
        return <DemoConfinedPanel />;
    }

    if (error) {
        return (
            <Box className="page-content">
                <Typography variant="h4" component="h2" gutterBottom>
                    My Policies
                </Typography>
                <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={() => fetchAssignments()}>
                    Try Again
                </Button>
            </Box>
        );
    }

    const pending = data?.pending ?? [];
    const completed = data?.completed ?? [];
    const declined = data?.declined ?? [];

    return (
        <Box className="page-content">
            <Typography variant="h4" component="h2" gutterBottom>
                My Policies
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Review and sign your pending policies.
            </Typography>

            {/* `signer-queue-list` — tests/e2e/core-loop.spec.ts (#2335).
                The signer's queue of pending policies is the surface the
                core-loop test asserts on after the admin's assignment fans
                out to the user-side. */}
            <Box component="section" sx={{ mb: 4 }} data-testid="signer-queue-list">
                <Typography variant="h5" component="h3" gutterBottom>
                    Pending
                </Typography>
                {pending.length === 0 ? (
                    <EmptyState
                        variant="success"
                        message="No pending policies — you are all caught up!"
                        hint="You'll be notified by email when a new policy needs your signature."
                    />
                ) : (
                    pending.map((assignment: SignerAssignment) => (
                        <PendingCard key={assignment.assignmentId} assignment={assignment} onViewDocument={handleViewDocument} loadingDocId={loadingDocId} />
                    ))
                )}
            </Box>

            <Box component="section" sx={{ mb: declined.length > 0 ? 4 : 0 }}>
                <Typography variant="h5" component="h3" gutterBottom>
                    Signed
                </Typography>
                {completed.length === 0 ? (
                    <EmptyState message="No signed policies yet." hint="Signed policies will appear here." />
                ) : (
                    completed.map((assignment: SignerAssignment) => (
                        <CompletedCard
                            key={assignment.assignmentId}
                            assignment={assignment}
                            onViewDocument={handleViewDocument}
                            onViewDetails={setSignTimeDetailsAssignment}
                            loadingDocId={loadingDocId}
                        />
                    ))
                )}
            </Box>

            {declined.length > 0 && (
                <Box component="section">
                    <Typography variant="h5" component="h3" gutterBottom>
                        Declined
                    </Typography>
                    {declined.map((assignment: SignerAssignment) => (
                        <DeclinedCard
                            key={assignment.assignmentId}
                            assignment={assignment}
                            onViewDocument={handleViewDocument}
                            onViewDetails={setSignTimeDetailsAssignment}
                            loadingDocId={loadingDocId}
                        />
                    ))}
                </Box>
            )}

            {/* Sign-time Details Modal (issue #1147) */}
            <Dialog
                open={signTimeDetailsAssignment !== null}
                onClose={() => setSignTimeDetailsAssignment(null)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="signer-sign-time-details-title"
            >
                <DialogTitle id="signer-sign-time-details-title">
                    Sign-time details
                    {signTimeDetailsAssignment && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {/* Issue #1845 — humanize the backend sentinel here too so the
                                modal opened from a missing-policy card never shows the raw
                                `(unknown policy)` string.
                                Issue #3225 — also humanize embedded ISO-8601 timestamps
                                (innermost) so the header matches the card title. */}
                            {displayPolicyTitle(cleanPolicyTitle(formatPolicyTitle(signTimeDetailsAssignment.policyTitle)))}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {signTimeDetailsAssignment && (
                        <SignTimeDetails
                            assignmentId={signTimeDetailsAssignment.assignmentId}
                            details={{
                                status: signTimeDetailsAssignment.status,
                                acknowledgedAt: signTimeDetailsAssignment.acknowledgedAt,
                                acknowledgedIp: signTimeDetailsAssignment.acknowledgedIp,
                                acknowledgedUserAgent: signTimeDetailsAssignment.acknowledgedUserAgent,
                                electronicSignatureConsented: signTimeDetailsAssignment.electronicSignatureConsented,
                                consentVersion: signTimeDetailsAssignment.consentVersion,
                                declinedAt: signTimeDetailsAssignment.declinedAt,
                                declineReason: signTimeDetailsAssignment.declineReason,
                                declinedIp: signTimeDetailsAssignment.declinedIp,
                                declinedUserAgent: signTimeDetailsAssignment.declinedUserAgent,
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSignTimeDetailsAssignment(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SignerPortal;
